import type { AgnoChatMessage } from "@/hooks/use-agno-chat";
import { isCanonical, mergeMessages } from "@/lib/message-store";

export interface MessageWindowCoverage {
  firstMessageId: string;
  lastMessageId: string;
  hasOlder: boolean;
  hasNewer: boolean;
  beforeCursor: string | null;
  afterCursor: string | null;
  startsAtTurnBoundary: boolean;
  endsAtTurnBoundary: boolean;
  partialTurn: boolean;
}

export interface StoredMessageWindow {
  key: string;
  messages: AgnoChatMessage[];
  coverage: MessageWindowCoverage;
  generation: string | null;
  requestGeneration: number;
  lastUsed: number;
}

export interface MessageWindowStore {
  activeWindowKey: string | null;
  requestGeneration: number;
  tail: AgnoChatMessage[];
  windows: Map<string, StoredMessageWindow>;
  newTailActivity: boolean;
}

export function createMessageWindowStore(tail: AgnoChatMessage[] = []): MessageWindowStore {
  return {
    activeWindowKey: null,
    requestGeneration: 0,
    tail,
    windows: new Map(),
    newTailActivity: false,
  };
}

function estimatedBytes(messages: readonly AgnoChatMessage[]): number {
  return messages.reduce(
    (total, message) => total + new TextEncoder().encode(message.content ?? "").byteLength,
    0,
  );
}

function trimWindows(
  windows: Map<string, StoredMessageWindow>,
  activeKey: string,
  maxWindows: number,
  maxBytes: number,
): Map<string, StoredMessageWindow> {
  const next = new Map(windows);
  const ordered = [...next.values()].sort((a, b) => a.lastUsed - b.lastUsed);
  let bytes = ordered.reduce((sum, window) => sum + estimatedBytes(window.messages), 0);
  for (const window of ordered) {
    if (next.size <= maxWindows && bytes <= maxBytes) break;
    if (window.key === activeKey) continue;
    next.delete(window.key);
    bytes -= estimatedBytes(window.messages);
  }
  return next;
}

export function activateMessageWindow(
  store: MessageWindowStore,
  window: StoredMessageWindow,
  options: { maxWindows?: number; maxBytes?: number } = {},
): MessageWindowStore {
  if (window.requestGeneration < store.requestGeneration) return store;
  const windows = new Map(store.windows);
  windows.set(window.key, {
    ...window,
    messages: mergeMessages([], window.messages),
  });
  return {
    ...store,
    activeWindowKey: window.key,
    requestGeneration: window.requestGeneration,
    windows: trimWindows(
      windows,
      window.key,
      options.maxWindows ?? 3,
      options.maxBytes ?? 8 * 1024 * 1024,
    ),
  };
}

export function mergeAdjacentWindow(
  store: MessageWindowStore,
  key: string,
  incoming: AgnoChatMessage[],
  coverage: MessageWindowCoverage,
  direction: "before" | "after",
  generation: string | null,
): MessageWindowStore {
  const current = store.windows.get(key);
  if (!current || current.generation !== generation) return store;
  const expectedCursor = direction === "before"
    ? current.coverage.beforeCursor
    : current.coverage.afterCursor;
  if (!expectedCursor) return store;
  const startsAtTurnBoundary = direction === "before"
    ? coverage.startsAtTurnBoundary
    : current.coverage.startsAtTurnBoundary;
  const endsAtTurnBoundary = direction === "after"
    ? coverage.endsAtTurnBoundary
    : current.coverage.endsAtTurnBoundary;
  const windows = new Map(store.windows);
  windows.set(key, {
    ...current,
    messages: mergeMessages(current.messages, incoming),
    coverage: {
      ...current.coverage,
      ...(direction === "before"
        ? {
            firstMessageId: coverage.firstMessageId,
            hasOlder: coverage.hasOlder,
            beforeCursor: coverage.beforeCursor,
            startsAtTurnBoundary: coverage.startsAtTurnBoundary,
          }
        : {
            lastMessageId: coverage.lastMessageId,
            hasNewer: coverage.hasNewer,
            afterCursor: coverage.afterCursor,
            endsAtTurnBoundary: coverage.endsAtTurnBoundary,
          }),
      partialTurn: !startsAtTurnBoundary || !endsAtTurnBoundary,
    },
  });
  return { ...store, windows };
}

export function updateIsolatedTail(
  store: MessageWindowStore,
  incoming: AgnoChatMessage[],
): MessageWindowStore {
  const hasOptimistic = store.tail.some((message) => !isCanonical(message));
  const tail = mergeMessages(
    store.tail,
    incoming,
    hasOptimistic && incoming.length > 0 ? { dropOptimistic: true } : undefined,
  );
  const newestBefore = store.tail.filter(isCanonical).at(-1)?.id;
  const newestAfter = tail.filter(isCanonical).at(-1)?.id;
  return {
    ...store,
    tail,
    newTailActivity: store.activeWindowKey !== null
      && newestAfter !== undefined
      && newestAfter !== newestBefore,
  };
}

export function selectLatestTail(store: MessageWindowStore): MessageWindowStore {
  return { ...store, activeWindowKey: null, newTailActivity: false };
}

export function activeWindowMessages(store: MessageWindowStore): AgnoChatMessage[] {
  if (!store.activeWindowKey) return store.tail;
  return store.windows.get(store.activeWindowKey)?.messages ?? store.tail;
}
